import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useUpdateReseller } from "@/api/reseller-manager";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createResellerSchema } from "@/lib/schemas/reseller.schema";
import { getDrfFieldErrors } from "@/utils/errors";

type EditResellerInitialValues = {
	id: number;
	email: string;
	company_name: string;
};

type EditResellerDialogProps = {
	reseller: EditResellerInitialValues | null;
	onOpenChange: (open: boolean) => void;
};

export function EditResellerDialog({
	reseller,
	onOpenChange,
}: EditResellerDialogProps) {
	const { t } = useTranslation();
	const [email, setEmail] = useState("");
	const [companyName, setCompanyName] = useState("");
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

	const update = useUpdateReseller();
	const open = reseller !== null;

	useEffect(() => {
		if (reseller) {
			setEmail(reseller.email);
			setCompanyName(reseller.company_name);
			setFieldErrors({});
		}
	}, [reseller]);

	function handleSubmit() {
		if (!reseller) return;
		setFieldErrors({});
		const result = createResellerSchema.safeParse({
			email: email.trim(),
			company_name: companyName.trim(),
		});
		if (!result.success) {
			toast.error(
				result.error.issues[0]?.message ?? t("common.validationError"),
			);
			return;
		}
		const patch: { email?: string; company_name?: string } = {};
		if (email.trim() !== reseller.email) patch.email = email.trim();
		if (companyName.trim() !== reseller.company_name)
			patch.company_name = companyName.trim();
		if (Object.keys(patch).length === 0) {
			onOpenChange(false);
			return;
		}
		update.mutate(
			{ id: reseller.id, data: patch },
			{
				onSuccess: () => {
					toast.success(t("resellers.updated"));
					onOpenChange(false);
				},
				onError: (error) => {
					const drfErrors = getDrfFieldErrors(error);
					setFieldErrors(drfErrors);
					toast.error(t("resellers.updateError"));
				},
			},
		);
	}

	const canSubmit = email.trim() && companyName.trim() && !update.isPending;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("resellers.editTitle")}</DialogTitle>
					<DialogDescription>
						{t("resellers.editDescription")}
					</DialogDescription>
				</DialogHeader>
				<div className="flex flex-col gap-4 py-2">
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="reseller-edit-email">
							{t("resellers.fieldEmail")}
						</Label>
						<Input
							id="reseller-edit-email"
							type="email"
							name="email"
							autoComplete="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							aria-invalid={!!fieldErrors.email}
							aria-describedby={
								fieldErrors.email ? "reseller-edit-email-error" : undefined
							}
						/>
						{fieldErrors.email && (
							<p
								id="reseller-edit-email-error"
								className="text-destructive text-sm"
								role="alert"
							>
								{fieldErrors.email}
							</p>
						)}
					</div>
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="reseller-edit-company">
							{t("resellers.fieldCompanyName")}
						</Label>
						<Input
							id="reseller-edit-company"
							name="company"
							autoComplete="organization"
							value={companyName}
							onChange={(e) => setCompanyName(e.target.value)}
							onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
							aria-invalid={!!fieldErrors.company_name}
							aria-describedby={
								fieldErrors.company_name
									? "reseller-edit-company-error"
									: undefined
							}
						/>
						{fieldErrors.company_name && (
							<p
								id="reseller-edit-company-error"
								className="text-destructive text-sm"
								role="alert"
							>
								{fieldErrors.company_name}
							</p>
						)}
					</div>
				</div>
				<DialogFooter>
					<Button
						size="sm"
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={update.isPending}
					>
						{t("resellers.cancel")}
					</Button>
					<Button onClick={handleSubmit} disabled={!canSubmit}>
						{update.isPending ? t("resellers.saving") : t("resellers.save")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
